/* eslint-disable @ngrx/avoid-dispatching-multiple-actions-sequentially */
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ToastController } from '@ionic/angular';
import { PushPipe } from '@ngrx/component';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { THDSCommonModule } from '@thds/common';
import { DlSpinner } from '@thds/components/spinner';
import { first, tap } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { handleHttpError } from '../../common/httpErrorHandler';
import { MonatsauswertungLayoutDto } from '../api/models/monatsauswertung-layout-dto';
import { PageHeaderComponent } from '../page-header/page-header.component';
import { AuswertungLayoutsActions } from '../store/auswertung-layouts/auswertung-layouts.actions';
import * as fromMonatsauswertungenLayouts from '../store/auswertung-layouts/auswertung-layouts.selectors';
import { AggregierteAuswertungenLayoutsEditorComponent } from './aggregierteauswertungen-layouts-editor/aggregierteauswertungen-layouts-editor.component';
import { AggregierteAuswertungenLayoutsListComponent } from './aggregierteauswertungen-layouts-list/aggregierteauswertungen-layouts-list.component';
import { MonatsauswertungLayoutsPageComponentStore } from './monatsauswertung-layouts-page.store';
import {
  EditorOutput,
  MonatsauswertungenLayoutsEditorComponent,
} from './monatsauswertungen-layouts-editor/monatsauswertungen-layouts-editor.component';
import { MonatsauswertungenLayoutsListComponent } from './monatsauswertungen-layouts-list/monatsauswertungen-layouts-list.component';

@Component({
  selector: 'thds-monatsauswertung-layouts-page',
  standalone: true,
  imports: [
    CommonModule,
    MonatsauswertungenLayoutsEditorComponent,
    AggregierteAuswertungenLayoutsEditorComponent,
    THDSCommonModule,
    DlSpinner,
    MonatsauswertungenLayoutsListComponent,
    AggregierteAuswertungenLayoutsListComponent,
    PushPipe,
    PageHeaderComponent,
    RouterLink,
    RouterLinkActive,
  ],
  templateUrl: './layouts-page.component.html',
  styleUrls: ['./layouts-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MonatsauswertungLayoutsPageComponentStore],
})
export class LayoutsPage implements OnInit {
  private readonly store = inject(Store);
  private readonly _actions$ = inject(Actions);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _toastService = inject(ToastController);
  private readonly _router = inject(Router);
  readonly componentStore = inject(MonatsauswertungLayoutsPageComponentStore);

  public readonly monatsauswertungenLayouts$ = this.store.select(
    fromMonatsauswertungenLayouts.selectMonatsauswertungLayouts,
  );

  public readonly aggregierteAuswertungenLayouts$ = this.store.select(
    fromMonatsauswertungenLayouts.selectAggregierteAuswertungenLayouts,
  );

  ngOnInit(): void {
    this.store.dispatch(
      AuswertungLayoutsActions.loadMonatsauswertungLayoutsUndSpalten(),
    );
    this.store.dispatch(
      AuswertungLayoutsActions.loadAggregierteAuswertungenLayoutsUndSpalten(),
    );

    this._actions$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        ofType(
          AuswertungLayoutsActions.loadMonatsauswertungLayoutsFailure,
          AuswertungLayoutsActions.loadAggregierteAuswertungenLayoutsFailure,
        ),
        tap(async () => await this.onLoadingFailure()),
      )
      .subscribe();

    this._actions$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        ofType(
          AuswertungLayoutsActions.updateMonatsauswertungLayoutFailure,
          AuswertungLayoutsActions.updateAggregierteAuswertungenLayoutFailure,
        ),
        tap(async ({ error }) => {
          await this.onFailure(
            error,
            'Beim Aktualisieren des Layouts ist ein Fehler aufgetreten',
          );
        }),
      )
      .subscribe();

    this._actions$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        ofType(
          AuswertungLayoutsActions.createMonatsauswertungLayoutFailure,
          AuswertungLayoutsActions.createAggregierteAuswertungenLayoutFailure,
        ),
        tap(async ({ error }) => {
          await this.onFailure(
            error,
            'Beim Erstellen des Layouts ist ein Fehler aufgetreten',
          );
        }),
      )
      .subscribe();

    this._actions$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        ofType(AuswertungLayoutsActions.createMonatsauswertungLayoutSuccess),
        tap(async () => {
          await this.onSuccess('Das Layout wurde erfolgreich erstellt');
        }),
        tap(async ({ monatsauswertungLayout }) => {
          await this._router.navigate([
            'layouts',
            'monatsauswertungen',
            monatsauswertungLayout.id,
          ]);
        }),
      )
      .subscribe();

    this._actions$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        ofType(
          AuswertungLayoutsActions.createAggregierteAuswertungenLayoutSuccess,
        ),
        tap(async () => {
          await this.onSuccess('Das Layout wurde erfolgreich erstellt');
        }),
        tap(async ({ aggregierteAuswertungenLayout }) => {
          await this._router.navigate([
            'layouts',
            'aggregierteauswertungen',
            aggregierteAuswertungenLayout.id,
          ]);
        }),
      )
      .subscribe();

    this._actions$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        ofType(
          AuswertungLayoutsActions.updateMonatsauswertungLayoutSuccess,
          AuswertungLayoutsActions.updateAggregierteAuswertungenLayoutSuccess,
        ),
        tap(async () => {
          await this.onSuccess('Das Layout wurde erfolgreich aktualisiert');
        }),
      )
      .subscribe();
  }

  onCreateMonatsauswertungen() {
    this._router.navigate(['/layouts/monatsauswertungen', 'create']);
  }

  onCreateAggregierteAuswertungen() {
    this._router.navigate(['/layouts/aggregierteauswertungen', 'create']);
  }

  onSubmit($event: EditorOutput) {
    this.componentStore.selectMode$
      .pipe(
        first(),
        withLatestFrom(this.componentStore.selectedMonatsauswertungLayoutId$),
        tap(([mode, id]) => {
          if (mode === 'monatsauswertungen-create') {
            this.onCreateLayout($event);
          } else {
            if (!id) {
              throw new Error('No id selected');
            }
            this.onUpdateLayout($event, id);
          }
        }),
      )
      .subscribe();
  }

  onSubmitAggregierteAuswertungen($event: EditorOutput) {
    this.componentStore.selectMode$
      .pipe(
        first(),
        withLatestFrom(
          this.componentStore.selectedAggregierteAuswertungLayoutId$,
        ),
        tap(([mode, id]) => {
          if (mode === 'aggregierteauswertungen-create') {
            this.onCreateAggregierteAuswertungenLayout($event);
          } else {
            if (!id) {
              throw new Error('No id selected');
            }
            this.onUpdateAggregierteAuswertungenLayout($event, id);
          }
        }),
      )
      .subscribe();
  }

  onSelectLayout(layout: MonatsauswertungLayoutDto) {
    this.componentStore.setSelectMonatsauswertungLayoutId(layout.id);
  }

  private onCreateLayout($event: EditorOutput) {
    this.store.dispatch(
      AuswertungLayoutsActions.createMonatsauswertungLayout({
        monatsauswertungLayout: {
          name: $event.name,
          kuerzel: $event.kuerzel,
          spalten: $event.spalten.map((x) => ({
            spalteId: x,
          })),
        },
      }),
    );
  }

  private onUpdateLayout($event: EditorOutput, id: string) {
    this.store.dispatch(
      AuswertungLayoutsActions.updateMonatsauswertungLayout({
        id,
        monatsauswertungLayout: {
          name: $event.name,
          kuerzel: $event.kuerzel,
          spalten: $event.spalten.map((x) => ({
            spalteId: x,
          })),
        },
      }),
    );
  }

  private onCreateAggregierteAuswertungenLayout($event: EditorOutput) {
    this.store.dispatch(
      AuswertungLayoutsActions.createAggregierteAuswertungenLayout({
        aggregierteAuswertungenLayout: {
          name: $event.name,
          kuerzel: $event.kuerzel,
          spalten: $event.spalten.map((x) => ({
            spalteId: x,
          })),
        },
      }),
    );
  }

  private onUpdateAggregierteAuswertungenLayout(
    $event: EditorOutput,
    id: string,
  ) {
    this.store.dispatch(
      AuswertungLayoutsActions.updateAggregierteAuswertungenLayout({
        id,
        aggregierteAuswertungenLayout: {
          name: $event.name,
          kuerzel: $event.kuerzel,
          spalten: $event.spalten.map((x) => ({
            spalteId: x,
          })),
        },
      }),
    );
  }

  private async onLoadingFailure() {
    const toast = await this._toastService.create({
      color: 'danger',
      message: 'Fehler beim Laden der Monatsauswertungen',
      duration: 3000,
    });

    await toast.present();
  }

  private async onSuccess(message: string) {
    const toast = await this._toastService.create({
      color: 'success',
      message,
      duration: 3000,
    });

    await toast.present();
  }

  private async onFailure(error: unknown, message: string) {
    if (error instanceof HttpErrorResponse) {
      await handleHttpError(error, this._toastService, message);
    } else {
      const toast = await this._toastService.create({
        message,
        color: 'danger',
        duration: 5000,
      });
      await toast.present();
    }
  }
}
